/**
 * Authentication provider
 * Handles login flow and protected routes
 */
import { type ReactNode, useEffect, useState } from 'react';
import { authApi } from '@/shared/api/auth-api';

interface AuthProviderProps {
  children: ReactNode;
}

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

function isTokenExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    // exp is in seconds, Date.now() in ms. Add 30s buffer.
    return !payload.exp || payload.exp * 1000 < Date.now() + 30000;
  } catch {
    return true;
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const authenticate = async () => {
      // Check if already authenticated with valid (non-expired) token
      if (authApi.isAuthenticated()) {
        const token = authApi.getToken();
        if (token && !isTokenExpired(token)) {
          setIsLoading(false);
          return;
        }
        // Token expired — clear and re-authenticate
        authApi.logout();
      }

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        if (cancelled) return;
        try {
          await authApi.login();
          if (!cancelled) setIsLoading(false);
          return;
        } catch (err) {
          console.warn(`Auth attempt ${attempt}/${MAX_RETRIES} failed:`, err);
          if (attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * attempt));
          } else if (!cancelled) {
            setError(err instanceof Error ? err.message : 'Authentication failed');
            setIsLoading(false);
          }
        }
      }
    };

    authenticate();
    return () => {
      cancelled = true;
    };
  }, []);

  // Show loading state
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: 'var(--tg-theme-bg-color, #ffffff)',
          color: 'var(--tg-theme-text-color, #000000)',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '16px' }}>🔐</div>
          <div>Authenticating...</div>
        </div>
      </div>
    );
  }

  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    // Re-trigger auth by remounting
    window.location.reload();
  };

  // Show error state
  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: 'var(--tg-theme-bg-color, #ffffff)',
          color: 'var(--tg-theme-text-color, #000000)',
        }}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ marginBottom: '16px', fontSize: '48px' }}>⚠️</div>
          <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>Authentication Error</div>
          <div style={{ fontSize: '14px', opacity: 0.7, marginBottom: '16px' }}>{error}</div>
          <button
            onClick={handleRetry}
            style={{
              padding: '10px 24px',
              borderRadius: '12px',
              border: 'none',
              background: 'var(--tg-theme-button-color, #3390ec)',
              color: 'var(--tg-theme-button-text-color, #ffffff)',
              fontWeight: 'bold',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Render app once authenticated
  return <>{children}</>;
}
